import { QueryFilter, DataType, EntityMetaDataModelOperator } from "../../../../../../../../models/queries/queries.models";
import { QueryBuilder } from "../../../../../../../components/queries/query-builder";
import { BindingSignaler } from "aurelia-templating-resources";
import { autoinject, transient, Disposable, computedFrom } from "aurelia-framework";
import { BaseQueryBuilder } from "./base-query-builder";
import { EditableFieldBinding, GridUtils } from "../../../../../../../components/data-grid/data-grid";
import { LookupClientService } from "../../../../../../../../services/lookups/lookup-client.service";
import { ManageRightShipUsersClientService } from "../../../../../../../../services/modules/righship-qi-manage-users.service";
import { CustomerService } from "../../../../../../../../services/modules/customer-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../../../configuration/event-messages.constants";
import { AllEntityTypeConfigurationConstants, QueryLookupsWithEF } from "../../../../../../../../models/basics/basics.models";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

enum AggregateOperator {
    Count = 1,
    Sum = 2,
    Min = 3,
    Max = 4,
    Avg = 5,
    First = 6,
    Last = 7,
    PartitionedCount = 8,
    FirstBy = 9,
    LastBy = 10    
}

@autoinject
@transient()
export class DefaultQueryBuilder extends BaseQueryBuilder<QueryFilter> {

    public allowSetDefault: boolean = false;

    constructor(private bindingSignaler: BindingSignaler, lookupService: LookupClientService, customerService: CustomerService,
        manageRightShipUsersClientService: ManageRightShipUsersClientService, private eventAggregator: EventAggregator, protected configService: ConfigService) {
        super(lookupService, customerService, manageRightShipUsersClientService, configService);
    }
    
    @computedFrom(Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.uri),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.filterProperty),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.selectedModel),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.selectedModel.filterProperties),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.selectedModel.filterProperties.length),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.filterProperty.lookupMappedToProperty),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.filterProperty.type))
    get valueProperties() {
        if (this.filter && this.filter.uri && this.filter.filterProperty && this.selectedModel) {
            const dataType = this.filter.filterProperty.type;
            const query = this.filter.filterProperty.lookupMappedToProperty;
            return this.selectedModel.filterProperties.filter(x => this.isMatchingDataType(x.type, dataType) && x.lookupMappedToProperty == query);
        }

        return null;
    }

    _subscriptionToDocumentClickEvent: Disposable;

    attached() {
        setTimeout(() => { this.allowSetDefault = true}, 1000);
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeFilters.bind(this));
    }

    detached() {
        this.allowSetDefault = false;
        if (this._subscriptionToDocumentClickEvent)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    closeFilters() {
        if (this.filter) {
            const filter = <any>this.filter;
            if (filter.showGhgRatingFilter)
                filter.showGhgRatingFilter = false;
        }
    }

    fixFilter(filter: QueryFilter, prop: string, prop2: string) {
        if (filter[prop])
            filter[prop2] = false;

        this.bindingSignaler.signal("update-filters-html");
    }

    operatorChanged($event) {
        if($event?.detail?.item != $event?.detail?.oldItem)
            this.bindingSignaler.signal("update-filters-html");
    }

    getPropertyOperators(x: QueryBuilder, filter: QueryFilter) {
        if (!filter) {
            return [];
        }

        const isPartitionedCount = filter?.isAggregate && filter?.aggregateOperator == AggregateOperator.PartitionedCount;

        //we override this so that we coerce In as operator when this is selected 
        if (!isPartitionedCount && filter && filter.filterProperty && (((<any>filter).isRuleParameter && (filter.filterProperty.type == DataType.Lookup || filter.filterProperty.type == DataType.EntityFinder)) || filter.filterProperty.type == DataType.GHGRatingSelector || filter.filterProperty.type == DataType.RatingSelector)) {
            const op: EntityMetaDataModelOperator = {
                friendlyName: "in",
                supportedTypes: [filter.filterProperty.type],
                translatedSymbol: "{0} in ({1})",
                requiresInterpolation: true,
                requiresFilterValue: true,
                isOnlyForNullableProperties: false
            };
            return [op];
        }
        return this.rulesVM.getPropertyOperators(x, filter);
    }

    @computedFrom(Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.uri),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.filterProperty),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.selectedModel),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.selectedModel.filterProperties),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.selectedModel.filterProperties.length),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.aggregateOperator))
    get filterProperties() {
        if (this.filter && this.filter.uri && this.selectedModel) {
            const aggregate = [AggregateOperator.Avg, AggregateOperator.Count, AggregateOperator.Max, AggregateOperator.Min, AggregateOperator.Sum];
            if (aggregate.indexOf(this.filter.aggregateOperator) > -1)
                return this.selectedModel.filterProperties.filter(x => this.isNumericType(x.type));

            return this.selectedModel.filterProperties;
        }
        return [];
    }

    isMatchingDataType(sourceType: DataType, targetType: DataType) {
        return (sourceType == targetType || (this.isNumericType(sourceType) && this.isNumericType(targetType)))
    }
    isNumericType(type: DataType) {
        return type == DataType.Number || type == DataType.Double;
    }

    @computedFrom(Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.vm),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.vm.models),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.vm.models.length),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.uri))
    get selectedModel() {
        if (this.filter && this.vm && this.vm.models)
            return this.vm.models.filter(x => x.uri == this.filter.uri)[0];
        return null;
    }

    @computedFrom(Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter),
        Reflection.getPropertyPath<DefaultQueryBuilder>(z => z.filter.aggregateOperator))
    get hasOrderBy() {
        const aggregate = [AggregateOperator.First, AggregateOperator.Last, AggregateOperator.FirstBy, AggregateOperator.LastBy];
        return this.filter && aggregate.indexOf(this.filter.aggregateOperator) > -1
    }

    public async filtersHtml(filter: any) {

        if (!filter || !filter.filterProperty) {
            return null;
        }

        if (filter.isRuleParameter && this.readonly)
            filter.filterValue = await this.getParameterValue(filter);

        filter.skipOnNullOrEmpty = !filter.filterProperty.isDynamicProperty && !filter.filterProperty.isNullable;

        const projectedProperty = this.vm.selectedModel && this.vm.selectedModel.projectionProperties
            ? this.vm.selectedModel.projectionProperties.filter(x => x.name == filter.filterProperty.name)[0]
            : null;

        const isLookup = filter.filterProperty.lookupMappedToProperty && filter.filterProperty.type != DataType.HierarchicalLookup;

        //when datatype is lookup and in is selected 
        const isMultiSelect = (filter.filterProperty.type == DataType.Lookup || filter.filterProperty.type == DataType.EntityFinder) &&
            filter.filterOperator &&
            ["in", "not in"].indexOf(filter.filterOperator.friendlyName) > -1;

        const isEditable = !this.readonly && !filter.isReadonly;
        const displayValue = await this.getDisplayValue(filter, projectedProperty);

        const isEntityFinder = filter.filterProperty.type == DataType.EntityFinder;// filter.filterProperty.entityFinderMappedToProperty ? true : false;

        let bindingType = "lookup: filter.filterProperty.lookupMappedToProperty";
        let value = "filter.filterProperty.lookupMappedToProperty";
        let query = "filter.filterProperty.lookupMappedToProperty";
        let lookup = filter && filter.filterProperty ? filter.filterProperty.lookupMappedToProperty : null;

        if (isEntityFinder) {
            lookup = filter && filter.filterProperty ? filter.filterProperty.entityFinderMappedToProperty : null;
            query = "filter.filterProperty.entityFinderMappedToProperty";
            bindingType = "entityFinder: filter.filterProperty.entityFinderMappedToProperty";
            value = "filter.filterProperty.entityFinderMappedToProperty";
        }

        const isPartitionedCount = this.filter && this.filter.aggregateOperator == AggregateOperator.PartitionedCount;

        const binding: EditableFieldBinding = {
            lookup: lookup,
            query: query,
            isEditable: isEditable,
            binding: `filter.filterValue | initFilter : {type: filter.filterProperty.type, contextPropertyValue: filter.filterContextPropertyValue, ${bindingType} }`,
            displayValueBinding: `'${displayValue}'`,
            value: value,
            dataType: isPartitionedCount
                ? DataType.Number
                : (isLookup ? DataType.Lookup : filter.filterProperty.type),
            parameter: "filter.filterValue",
            secondParameter: this.getSecondParameter(filter, projectedProperty),
            isNotRangeDatePicker: true,
            isSecondParameterBindable: false,
            startLabel: filter.filterProperty.startLabel,
            endLabel: filter.filterProperty.endLabel,
            isNullable: filter.filterProperty.isNullable,
            isDisabled: this.disabled,
            isValueCollection: isMultiSelect,
            isPropertyCollection: isPartitionedCount? false: filter.filterProperty.isCollection
        };

        if (filter.filterProperty.type == DataType.Boolean && !isEditable && !isPartitionedCount)
            return `<div>${displayValue}</div>`;

        if (filter.filterProperty.type == DataType.GHGRatingSelector && !isPartitionedCount) {
            return `<div class="filter">
                        <div if.bind="!readonly && !disabled" class="filter-value row align-items-middle \${filter.showGhgRatingFilter ? 'active' : '' }" 
                            click.delegate="ghgSelect($event, filter)">
                            <span class="comp-vessel-type-value-text \${ghg.externalLabel && filter.filterValue && filter.filterValue.length > 0? '':'placeholder'}">
                            \${ghg.externalLabel && filter.filterValue && filter.filterValue.length > 0? ghg.externalLabel: "Select"}</span>
                            <i class="icon-dropdown material-icons">keyboard_arrow_down</i>
                        </div>
                        <ghg-rating-selector readonly.bind="readonly || disabled" click.delegate="$event.stopPropagation()" 
                            show.bind="filter.showGhgRatingFilter || readonly || disabled" selected-rating-weights.two-way="filter.filterValue" 
                            show-action-buttons.bind="false" selected-items.two-way="filter.ghgSelectedItems" view-model.ref="ghg"></ghg-rating-selector>
                    </div>`;
        }

        //Show custom field for Safety Score
        if (filter.filterProperty.type == DataType.RatingSelector && !isPartitionedCount) {
            return `<div class="filter">

                <div class="filter-value row align-items-middle \${filter.showGhgRatingFilter ? 'active':'' }" 
                    click.trigger="ghgSelect($event, filter)" if.bind="!readonly && !disabled">
                    <span class="comp-vessel-type-value-text \${ss.externalLabel && filter.filterValue && filter.filterValue.length > 0? '':'placeholder'}">
                    \${ss.externalLabel && filter.filterValue && filter.filterValue.length > 0? ss.externalLabel: "Select"}</span>
                    <i class="icon-dropdown material-icons">keyboard_arrow_down</i>
                </div>

                <rating-selector selected-item-ids.two-way="filter.filterValue" property-name="Safety Ratings" min="1" max="5" 
                    click.delegate="$event.stopPropagation()" view-model.ref="ss"
                    show.bind="filter.showGhgRatingFilter || readonly || disabled" external-label.from-view="safetyRatingLabel" 
                    show-action-buttons.bind="false" start-external-label="Safety Score" readonly.bind="readonly || disabled">
                </rating-selector>
            </div>`;
        }

        const control = GridUtils.getControl(binding, true);
        return control;
    }

    public getEntityFinderTypes(uri: string) {
        const queryLookup = QueryLookupsWithEF.filter(x => x.lookup == uri);
        if (queryLookup.length > 0) {
            const entity = AllEntityTypeConfigurationConstants.filter(x => x.uri == queryLookup[0].odataUri)[0];
            const clonedEntity = CloneHelper.clone(entity);
            return [clonedEntity];
        }

        const entity = AllEntityTypeConfigurationConstants.filter(x => x.uri == uri);
        return entity;
    }

    ghgSelect = ((e, item) => { e.stopPropagation(); item.showGhgRatingFilter = !item.showGhgRatingFilter; });
}
